/**
 * Raid Lobby View
 *
 * Multiplayer lobby where players wait before starting a raid.
 * Features:
 * - Invite code display (copyable)
 * - 4 player slots with ready states
 * - Difficulty selector (host only)
 * - Start button (host only, when all ready)
 */

import { useEffect, useState, useCallback } from "react";
import { useSPAContext } from "@/providers/spa-provider";
import { useBackAction } from "@/hooks/use-back-action";
import {
  useRaid,
  useRaidLobby,
  RaidLobbyPlayer,
} from "@/providers/raid-provider";
import { RouteNames } from "@/types/route-names";
import { Button } from "@/components/ui/buttons/button";
import { CountryFlag } from "@/components/ui/country-flag/country-flag";
import { RaidDifficulty } from "types/raids/types";
import { PlayerRole } from "types/raid-dynamodb";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { Direction } from "types/battle-engine/battle";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { isXsScreen } from "@/utils/screen-size-check";

/** Difficulty display configuration */
const DIFFICULTY_CONFIG: Record<
  RaidDifficulty,
  { label: string; color: string; bgColor: string; borderColor: string }
> = {
  [RaidDifficulty.EASY]: {
    label: "Easy",
    color: "text-pk-green-5",
    bgColor: "bg-pk-green-1",
    borderColor: "border-pk-green-3",
  },
  [RaidDifficulty.NORMAL]: {
    label: "Normal",
    color: "text-pk-blue-5",
    bgColor: "bg-pk-blue-1",
    borderColor: "border-pk-blue-3",
  },
  [RaidDifficulty.HARD]: {
    label: "Hard",
    color: "text-pk-orange-4",
    bgColor: "bg-pk-orange-1",
    borderColor: "border-pk-orange-3",
  },
  [RaidDifficulty.EXPERT]: {
    label: "Expert",
    color: "text-pk-red-4",
    bgColor: "bg-pk-red-1",
    borderColor: "border-pk-red-3",
  },
  [RaidDifficulty.EPIC]: {
    label: "Epic",
    color: "text-pk-purple-4",
    bgColor: "bg-pk-purple-1",
    borderColor: "border-pk-purple-3",
  },
};

const PLAYER_SLOTS: PlayerRole[] = [
  "player_a",
  "player_b",
  "player_c",
  "player_d",
];

export const RaidLobbyView = () => {
  const { setCurrentRoute } = useSPAContext();
  const { raidState } = useRaid();
  const {
    players,
    isHost,
    yourRole,
    difficulty,
    raidName,
    inviteCode,
    canStart,
    toggleReady,
    setDifficulty,
    startRaid,
    leaveRaid,
  } = useRaidLobby();

  const [copiedCode, setCopiedCode] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [showDifficultyPicker, setShowDifficultyPicker] = useState(false);

  /**
   * Leave the raid lobby (used by both Leave button and back action)
   */
  const handleLeave = useCallback(() => {
    setIsLeaving(true);
    leaveRaid();
    setCurrentRoute(RouteNames.RAID_START);
  }, [leaveRaid, setCurrentRoute]);

  // Register back action to properly leave the lobby when back button is pressed
  useBackAction(handleLeave);

  /**
   * Auto-navigate to unified raid route when raid starts
   */
  useEffect(() => {
    if (
      raidState.status === "starting" ||
      raidState.status === "room_selection" ||
      raidState.status === "draft" ||
      raidState.status === "in_battle"
    ) {
      setCurrentRoute(RouteNames.RAID_ACTIVE);
    }
  }, [raidState.status, setCurrentRoute]);

  /**
   * Copy invite code to clipboard
   */
  const handleCopyCode = async () => {
    if (!inviteCode) return;

    try {
      await navigator.clipboard.writeText(inviteCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (err) {
      console.error("[RaidLobbyView] Failed to copy:", err);
    }
  };

  /**
   * Start the raid (host only)
   */
  const handleStart = () => {
    startRaid();
  };

  /**
   * Select difficulty (host only)
   */
  const handleDifficultySelect = (diff: RaidDifficulty) => {
    setDifficulty(diff);
    setShowDifficultyPicker(false);
  };

  // Get current player's ready state
  const currentPlayer = yourRole ? players[yourRole] : null;
  const isReady = currentPlayer?.is_ready ?? false;

  return (
    <div className="flex flex-col h-screen w-full relative overflow-hidden bg-pk-blue-3">
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Dot pattern overlay - large dots fading out toward bottom */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "radial-gradient(#000 7px, transparent 7px)",
            backgroundSize: "14px 14px",
            opacity: 0.15,
            maskImage: "linear-gradient(to bottom, black 0%, transparent 30%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, black 0%, transparent 60%)",
          }}
        />
        {/* Dot pattern overlay - small dots fading in toward bottom */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "radial-gradient(#fff 1.5px, transparent 1.5px)",
            backgroundSize: "4px 4px",
            opacity: 0.08,
            maskImage:
              "linear-gradient(to bottom, transparent 40%, black 100%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 40%, black 100%)",
          }}
        />
      </div>

      {/* Header Card */}
      <div
        className=" relative z-10 mb-2 xs:mb-4 border-b-4 border-pk-gray-4 flex-shrink-0"
        style={{
          backgroundImage: `url('/assets/raids/backgrounds/${raidState.raid_type?.toLowerCase()}.webp')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="bg-pk-gray-1/50 p-2 rounded-xl shadow-cartoon">
          <div className="flex items-start justify-between mb-2 xs:mb-4">
            <div>
              <h1 className="text-xl xs:text-3xl font-bold text-pk-gray-5 text-shadow  tracking-wider">
                {raidName || "Raid Lobby"}
              </h1>
            </div>

            {/* Difficulty Badge */}
            <button
              onClick={() => isHost && setShowDifficultyPicker(true)}
              disabled={!isHost}
              className={`
                px-2 xs:px-4 py-1 xs:py-2 rounded-lg border-b-4 border-r-2 border-l-2 border-t-2 transition-all active:border-b-2 active:translate-y-[2px]
                ${DIFFICULTY_CONFIG[difficulty].bgColor}
                ${DIFFICULTY_CONFIG[difficulty].borderColor}
                ${
                  isHost
                    ? "cursor-pointer hover:brightness-110"
                    : "cursor-default"
                }
              `}
            >
              <div className="flex flex-col items-center">
                <span className="text-[10px] uppercase text-pk-gray-5/80 font-bold tracking-widest">
                  Difficulty
                </span>
                <span
                  className={`font-bold text-sm xs:text-lg leading-none ${DIFFICULTY_CONFIG[difficulty].color} text-shadow`}
                >
                  {DIFFICULTY_CONFIG[difficulty].label}
                </span>
              </div>
            </button>
          </div>

          {/* Invite Code Ticket */}
          {inviteCode && (
            <div className=" relative group">
              <button
                onClick={handleCopyCode}
                className="relative w-full flex items-center justify-between px-2 xs:px-4 py-1 xs:py-2 bg-pk-gray-2 border-2 border-pk-yellow-3 rounded-lg hover:translate-y-[2px] hover:translate-x-[2px] transition-transform active:translate-y-1 active:translate-x-1"
              >
                <div className="flex items-center gap-3">
                  <div className="text-left flex gap-2 items-center">
                    <p className="text-pk-yellow-3 text-[10px] font-bold uppercase tracking-widest">
                      Invite Code
                    </p>
                    <p className="text-pk-gray-5 font-mono text-sm xs:text-xl tracking-widest font-bold">
                      {inviteCode}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-pk-gray-4 text-xs font-bold bg-pk-gray-1 px-2 py-1 rounded border border-pk-gray-3">
                    {copiedCode ? "COPIED!" : "CLICK TO COPY"}
                  </span>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Player Slots - Scrollable Content */}
      <div className="flex-1 px-2 pb-24 overflow-y-auto space-y-2 min-h-0">
        {PLAYER_SLOTS.map((role, index) => (
          <PlayerSlot
            key={role}
            player={players[role]}
            slotNumber={index + 1}
            isYou={role === yourRole}
            isHost={role === "player_a"}
          />
        ))}
      </div>

      {/* Action Buttons - Fixed at bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-2 flex justify-center items-center gap-4 bg-pk-blue-3/95 backdrop-blur-sm border-t border-pk-gray-4/30 z-20">
        {/* Ready / Start Button */}
        {isHost ? (
          <Button
            variant={canStart ? "green" : "gray"}
            onClick={handleStart}
            disabled={!canStart}
            className=""
          >
            <span className="text-xs xs:text-lg font-bold uppercase tracking-wide">
              {canStart ? "Start Raid!" : "Waiting..."}
            </span>
          </Button>
        ) : (
          <Button
            variant={isReady ? "red" : "green"}
            onClick={toggleReady}
            className=""
          >
            <span className="text-xs xs:text-lg font-bold uppercase tracking-wide">
              {isReady ? "Cancel" : "Ready!"}
            </span>
          </Button>
        )}

        {/* Leave Button */}
        <Button
          variant="garnet"
          onClick={handleLeave}
          loading={isLeaving}
          className=""
        >
          <span className="text-xs xs:text-lg font-bold uppercase tracking-wide">
            Leave
          </span>
        </Button>
      </div>

      {/* Difficulty Picker Modal */}
      {showDifficultyPicker && (
        <DifficultyPicker
          currentDifficulty={difficulty}
          onSelect={handleDifficultySelect}
          onClose={() => setShowDifficultyPicker(false)}
        />
      )}
    </div>
  );
};

/**
 * Player slot component
 */
interface PlayerSlotProps {
  player: RaidLobbyPlayer | null;
  slotNumber: number;
  isYou: boolean;
  isHost: boolean;
}

const PlayerSlot = ({ player, slotNumber, isYou, isHost }: PlayerSlotProps) => {
  if (!player) {
    return (
      <div className="h-[50px] xs:h-[100px] flex items-center gap-4 p-2 xs:p-4 bg-pk-gray-1/40 border-2 border-dashed border-pk-gray-3/30 rounded-xl hover:bg-pk-gray-1/60 transition-colors group">
        <div className="w-10 xs:w-16 h-10 xs:h-16 rounded-xl bg-pk-gray-2/30 flex items-center justify-center border-2 border-dashed border-pk-gray-3/20 group-hover:border-pk-gray-3/50 transition-colors">
          <span className="text-pk-gray-3/50 text-xl xs:text-3xl font-bold group-hover:scale-110 transition-transform">
            {slotNumber}
          </span>
        </div>
        <div className="flex-1">
          <p className="text-pk-gray-3/50 font-bold text-sm xs:text-xl uppercase tracking-wide">
            Empty Slot
          </p>
          <p className="text-pk-gray-3/30 text-sm font-bold">
            Waiting for player...
          </p>
        </div>
      </div>
    );
  }

  const borderColor = isYou
    ? "border-pk-blue-4"
    : isHost
    ? "border-pk-yellow-4"
    : "border-pk-gray-3";
  const bgColor =
    player.is_ready || isHost
      ? "bg-pk-green-3/90"
      : isYou
      ? "bg-pk-blue-1/90"
      : isHost
      ? "bg-pk-gray-2/90"
      : "bg-pk-gray-1/90";

  return (
    <div
      className={`
        relative h-[75px] xs:h-[100px] flex items-center gap-4 p-1 pr-3 rounded-xl border-b-4 border-r-2 border-l-2 border-t-2 transition-all
        ${borderColor}
        ${bgColor}
        shadow-cartoon hover:brightness-105
      `}
    >
      {/* Avatar / Country */}
      <div className="relative flex flex-col items-center gap-1  -translate-y-4">
        <div className="w-8 xs:w-10 h-8 xs:h-10 rounded-xl bg-pk-gray-2 border-2 border-pk-gray-3 flex items-center justify-center overflow-hidden shadow-inner">
          {player.country ? (
            <CountryFlag
              countryCode={player.country}
              size={isXsScreen() ? "sm" : "md"}
            />
          ) : (
            <span className="text-2xl">👤</span>
          )}
        </div>

        {/* Badges */}
        <div className="absolute -top-2 -left-2 flex gap-1 z-10">
          {isHost && (
            <div
              className="bg-pk-yellow-3 text-pk-gray-1 w-6 h-6 flex items-center justify-center rounded-full border-2 border-pk-yellow-4 shadow-sm"
              title="Host"
            >
              <span className="text-xs">👑</span>
            </div>
          )}
        </div>
        {isYou && (
          <div
            className="bg-pk-blue-4 text-pk-gray-1 px-2 py-0.5 rounded-md border border-pk-blue-5 shadow-sm mt-[-10px] z-10"
            title="You"
          >
            <span className="block text-[8px] xs:text-[10px] font-bold leading-3 text-center uppercase tracking-wider">
              YOU
            </span>
          </div>
        )}
      </div>

      {/* Player Info & Team */}
      <div className="flex-1 min-w-0 flex flex-col justify-center h-full py-1">
        <div className="flex items-center justify-between mb-1 -translate-y-5">
          <StrokedText
            strokeWidth={10}
            fontSize={isXsScreen() ? 16 : 24}
            fillColor="#f4eae7"
            strokeColor="#242424"
          >
            {player.username}
          </StrokedText>
        </div>
      </div>

      {/* Team Preview - Pokemon Sprites */}
      <div className="flex items-end gap-1 mt-auto h-0 xs:h-5 pl-1 z-12 absolute bottom-2 left-0 w-full justify-around">
        {player.team_preview.map((poke, i) => (
          <div
            key={i}
            className="relative w-10 h-10 flex items-end justify-center group/poke"
            title={`${poke.base_pokemon_name} (Lvl ${poke.level})`}
          >
            <div className="w-8 xs:w-12 h-10 xs:h-16 flex items-end justify-center transform translate-y-2 transition-transform group-hover/poke:-translate-y-1">
              <PokemonSprite
                pokemon={{ base_pokemon_name: poke.base_pokemon_name }}
                animation="Idle"
                direction={Direction.SE}
                showShadow={true}
                style={{ scale: isXsScreen() ? 1.25 : 1.75 }}
              />
            </div>
            <div className="absolute -bottom-2 right-0 bg-black/60 text-white text-[10px] xs:text-xs w-max px-1 rounded border border-white/20">
              Lv. {poke.level}
            </div>
          </div>
        ))}
        {/* Empty slots filler */}
        {Array.from({
          length: Math.max(0, 6 - player.team_preview.length),
        }).map((_, i) => (
          <div
            key={`empty-${i}`}
            className="w-8 xs:w-10 h-8 xs:h-10 rounded-full bg-black/5 border-2 border-black/5 mx-0.5"
          />
        ))}
      </div>

      {/* Ready Status */}
      <div className="absolute right-1 top-1 flex flex-col items-center justify-center">
        {player.is_ready || isHost ? (
          <div className="bg-pk-green-5/80 px-2 py-0.5 rounded-lg border-2 border-pk-green-4 animate-bounce-sm">
            <span className="text-pk-green-1 font-bold drop-shadow-sm">
              ✓ Ready
            </span>
          </div>
        ) : (
          <div className="w-10 h-10 rounded-full border-2 border-dashed border-pk-gray-4/30 flex items-center justify-center">
            <span className="text-pk-gray-4/30 text-sm font-bold animate-pulse">
              ...
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Difficulty picker modal
 */
interface DifficultyPickerProps {
  currentDifficulty: RaidDifficulty;
  onSelect: (difficulty: RaidDifficulty) => void;
  onClose: () => void;
}

const DifficultyPicker = ({
  currentDifficulty,
  onSelect,
  onClose,
}: DifficultyPickerProps) => {
  const difficulties = Object.values(RaidDifficulty);

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-pk-gray-1 border-4 border-pk-gray-2 rounded-2xl p-6 w-full max-w-sm shadow-cartoon-lg relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Background Pattern */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-pk-blue-3 via-pk-purple-3 to-pk-red-3"></div>

        <h3 className="text-2xl text-pk-gray-5 font-bold text-center mb-6 text-shadow uppercase tracking-wide">
          Select Difficulty
        </h3>

        <div className="space-y-3">
          {difficulties.map((diff) => (
            <button
              key={diff}
              onClick={() => onSelect(diff)}
              className={`
                w-full p-4 rounded-xl border-b-4 border-r-2 border-l-2 border-t-2 transition-all text-left group relative overflow-hidden
                ${
                  diff === currentDifficulty
                    ? "border-pk-yellow-3 bg-pk-gray-2"
                    : "border-pk-gray-2 hover:border-pk-gray-3 bg-pk-gray-1/50"
                }
              `}
            >
              {/* Active Indicator */}
              {diff === currentDifficulty && (
                <div className="absolute left-0 top-0 bottom-0 w-2 bg-pk-yellow-3"></div>
              )}

              <div className="flex justify-between items-center pl-2">
                <div>
                  <span
                    className={`font-bold text-lg ${DIFFICULTY_CONFIG[diff].color} text-shadow block mb-1`}
                  >
                    {DIFFICULTY_CONFIG[diff].label}
                  </span>
                  <p className="text-pk-gray-4 text-xs font-medium">
                    {getDifficultyDescription(diff)}
                  </p>
                </div>
                {diff === currentDifficulty && (
                  <span className="text-pk-yellow-3 text-xl">◄</span>
                )}
              </div>
            </button>
          ))}
        </div>

        <button
          onClick={onClose}
          className="w-full mt-6 p-3 text-pk-gray-4 hover:text-pk-gray-5 font-bold uppercase tracking-wider transition-colors border-2 border-transparent hover:border-pk-gray-2 rounded-lg"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

/**
 * Get difficulty description text
 */
function getDifficultyDescription(difficulty: RaidDifficulty): string {
  switch (difficulty) {
    case RaidDifficulty.EASY:
      return "Lvl 30 • Low rewards";
    case RaidDifficulty.NORMAL:
      return "Lvl 45 • Medium rewards";
    case RaidDifficulty.HARD:
      return "Lvl 70 • High rewards";
    case RaidDifficulty.EXPERT:
      return "Lvl 100 • Very high rewards";
    case RaidDifficulty.EPIC:
      return "Lvl 150 • Max rewards";
    default:
      return "";
  }
}
